// Plain-HTML fallback derived from a block design.
//
// NOT the real email serializer (that renders the full visual design and is
// a backend deliverable): this extracts the words and links from a design
// into simple paragraphs so an automation built with the designer still
// sends a sensible email through the current text pipeline, and acts as the
// text alternative for older email apps later.

const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const para = (text) => `<p>${escapeHtml(text)}</p>`
const heading = (text) => `<p><strong>${escapeHtml(text)}</strong></p>`
const link = (label, url) => `<p><a href="${escapeHtml(url || '{{BookingLink}}')}">${escapeHtml(label)}</a></p>`

// The new email-native sections ("s-" types): their words and links in
// reading order. Formatting marks (*italic*, **bold**, [label](url)) are
// turned into plain words here; the designed email itself goes as emailHtml.
const plain = (t) => String(t || '')
  .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1 ($2)')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/\*([^*\n]+)\*/g, '$1')
const words = (parts, text) => { if (text) String(text).split(/\n\s*\n/).forEach(p => { if (p.trim()) parts.push(para(plain(p.trim()))) }) }
const sectionParts = (type, c, parts) => {
  const head = (t) => { if (t) parts.push(heading(plain(t).replace(/\n/g, ' '))) }
  const btn = (label, url) => { if (label) parts.push(link(label, url)) }
  switch (type) {
    case 's-header': case 's-divider': case 's-wordmark': return
    case 's-banner': words(parts, c.text); btn(c.link, c.url); return
    case 's-image': words(parts, c.caption); return
    case 's-button': head(c.heading); words(parts, c.body); btn(c.text, c.url); words(parts, c.note); return
    case 's-signoff': words(parts, c.text); words(parts, [c.name, c.role].filter(Boolean).join(', ')); return
    case 's-offer':
      words(parts, c.eyebrow); head(c.value); words(parts, c.subtitle)
      if (c.code) parts.push(para(`Use code ${c.code}`))
      words(parts, c.expiry); btn(c.button, c.buttonUrl); words(parts, c.terms); return
    case 's-menu':
      words(parts, c.eyebrow); head(c.heading)
      ;(c.items || []).forEach(it => parts.push(para([it.name, it.desc, it.detail, it.price].filter(Boolean).join(', '))))
      words(parts, c.note); btn(c.button, c.buttonUrl); return
    case 's-voucher': words(parts, c.eyebrow); head(c.value); words(parts, c.title); words(parts, c.note); btn(c.button, c.buttonUrl); return
    case 's-quote': if (c.quote) parts.push(para(`"${plain(c.quote)}"${c.name ? ` ${c.name}` : ''}${c.detail ? `, ${c.detail}` : ''}`)); return
    case 's-cards':
      words(parts, c.eyebrow); head(c.heading)
      words(parts, c.intro)
      ;(c.items || []).forEach(it => { head(it.title); words(parts, it.text); words(parts, [it.price, it.detail].filter(Boolean).join(', ')); btn(it.link, it.url) }); return
    case 's-team':
      words(parts, c.eyebrow); head(c.heading)
      ;(c.members || []).forEach(m => { head([m.name, m.role].filter(Boolean).join(', ')); words(parts, m.bio) })
      btn(c.button, c.buttonUrl); return
    case 's-steps':
      words(parts, c.eyebrow); head(c.heading)
      ;(c.steps || []).forEach((st, i) => { head(`${i + 1}. ${st.title || ''}`); words(parts, st.text) }); return
    case 's-stats': (c.items || []).forEach(it => parts.push(para([it.value, it.label].filter(Boolean).join(' ')))); return
    case 's-info':
      words(parts, c.eyebrow); head(c.heading); words(parts, c.address); words(parts, c.phone); words(parts, c.email); words(parts, c.hours)
      btn(c.button, c.buttonUrl); return
    case 's-feature':
      words(parts, c.eyebrow); head(c.heading); words(parts, c.panelWords && !c.image ? c.panelWords.replace(/\n/g, ' ') : '')
      head(c.title); words(parts, c.text); btn(c.link, c.url); return
    case 's-social': head(c.heading); (c.links || []).forEach(l => btn(l.label, l.url)); return
    case 's-gallery': words(parts, c.eyebrow); head(c.heading); return
    default:
      // s-hero, s-text, s-split and anything newer: the common fields
      words(parts, c.eyebrow); head(c.headline || c.heading); words(parts, c.body)
      btn(c.button, c.buttonUrl); btn(c.link, c.linkUrl)
  }
}

export const blocksToFallbackHtml = (blocks) => {
  if (!Array.isArray(blocks)) return ''
  const parts = []
  blocks.forEach((block) => {
    const c = block.content || {}
    // Footer is excluded: the sending pipeline appends its own footer
    if (block.type === 'footer') return
    if (typeof block.type === 'string' && block.type.startsWith('s-')) {
      sectionParts(block.type, c, parts)
      return
    }

    if (c.eyebrow) parts.push(para(c.eyebrow))
    if (c.headline) parts.push(heading(c.headline.replace(/\n/g, ' ')))
    if (c.heading) parts.push(heading(c.heading))
    if (c.subtext) parts.push(para(c.subtext))
    if (c.body) parts.push(para(c.body))
    if (c.text && block.type !== 'footer' && block.type !== 'button') parts.push(para(c.text))
    if (c.quote) parts.push(para(`"${c.quote}"${c.name ? ` ${c.name}` : ''}`))

    if (block.type === 'service' || block.type === 'product') {
      const bits = [c.name, c.price ? `${c.currency || '£'}${c.price}` : ''].filter(Boolean)
      if (bits.length) parts.push(para(bits.join(', ')))
      if (c.description) parts.push(para(c.description))
    }
    if (block.type === 'voucher' && c.buttonText) {
      parts.push(`<p><a href="${escapeHtml(c.url || '{{BookingLink}}')}">${escapeHtml(c.buttonText)}</a></p>`)
    }
    const buttonText = c.buttonText || (block.type === 'button' ? c.text : '')
    const buttonUrl = c.buttonUrl || (block.type === 'button' ? c.url : '')
    if (buttonText && block.type !== 'voucher') {
      parts.push(`<p><a href="${escapeHtml(buttonUrl || '{{BookingLink}}')}">${escapeHtml(buttonText)}</a></p>`)
    }
  })
  // Dedupe consecutive identical paragraphs (some blocks share fields)
  return parts.filter((p, i) => p !== parts[i - 1]).join('')
}

export default blocksToFallbackHtml
